
/**
 * SEO utilities for structured data and schema.org markup
 */

/**
 * Generates structured data markup for blog articles 
 */
export const generateArticleSchema = (
  title: string,
  description: string, 
  imageUrl: string,
  publishDate: string,
  canonicalUrl: string,
  language: string,
  keywords: string
): void => {
  const schema = {
    "@context": "https://schema.org",
    "@type": "Article",
    "headline": title,
    "description": description,
    "image": imageUrl,
    "datePublished": formatSchemaDate(publishDate),
    "dateModified": formatSchemaDate(publishDate),
    "author": {
      "@type": "Organization",
      "name": "Future Level",
      "url": "https://future-level.ch"
    },
    "publisher": {
      "@type": "Organization",
      "name": "Future Level",
      "logo": {
        "@type": "ImageObject",
        "url": "https://future-level.ch/logo.png"
      }
    },
    "mainEntityOfPage": {
      "@type": "WebPage",
      "@id": canonicalUrl
    },
    "inLanguage": language,
    "keywords": keywords
  };
  
  let scriptTag = document.getElementById('article-schema') as HTMLScriptElement | null;
  
  if (!scriptTag) {
    scriptTag = document.createElement('script');
    scriptTag.id = 'article-schema';
    scriptTag.setAttribute('type', 'application/ld+json');
    document.head.appendChild(scriptTag);
  }
  
  scriptTag.textContent = JSON.stringify(schema);
};

/**
 * Format date for schema.org (YYYY-MM-DD)
 */
export const formatSchemaDate = (dateString: string): string => {
  // Convert from DD.MM.YYYY to YYYY-MM-DD
  if (/^\d{2}\.\d{2}\.\d{4}$/.test(dateString)) {
    const parts = dateString.split('.');
    return `${parts[2]}-${parts[1]}-${parts[0]}`;
  }
  return dateString;
};

/**
 * Generates schema markup for service pages with specific industry focus
 * Based on the keyword research for targeted industry verticals
 */
export const generateIndustryServiceSchema = (
  industry: 'finance' | 'luxury' | 'tourism' | 'healthcare' | 'ecommerce' | 'general',
  title: string,
  description: string,
  imageUrl: string,
  canonicalUrl: string,
  language: string,
  city?: string
): void => {
  // Industry-specific details
  const industryDetails: {
    [key: string]: {
      name: string;
      description: string;
      keywords: string;
      serviceType: string;
      audience: string;
      faqs: Array<{question: string, answer: string}>; // Added FAQs for each industry
    };
  } = {
    finance: {
      name: language === 'de' ? 'KI-Videomarketing für Finanzdienstleister' :
            language === 'en' ? 'AI Video Marketing for Financial Services' :
            language === 'fr' ? 'Marketing Vidéo IA pour Services Financiers' :
            'Marketing Video AI per Servizi Finanziari',
      description: language === 'de' ? 'Professionelle KI-generierte Videos für Banken, Versicherungen und Finanzberater' : 
                  language === 'en' ? 'Professional AI-generated videos for banks, insurance companies, and financial advisors' : 
                  language === 'fr' ? 'Vidéos professionnelles générées par IA pour banques, assurances et conseillers financiers' : 
                  'Video professionali generati da IA per banche, assicurazioni e consulenti finanziari',
      keywords: 'KI-Videomarketing Finanzsektor, Banken Marketing Videos KI, Versicherung Erklärvideo',
      serviceType: 'Financial Services Video Marketing',
      audience: 'Banks, Insurance Companies, Financial Advisors',
      faqs: [
        {
          question: language === 'de' ? 'Wie können KI-Videos Finanzprodukte erklären?' : 
                    language === 'en' ? 'How can AI videos explain financial products?' :
                    language === 'fr' ? 'Comment les vidéos IA peuvent-elles expliquer les produits financiers?' :
                    'Come possono i video AI spiegare i prodotti finanziari?',
          answer: language === 'de' ? 'KI-Videos können komplexe Finanzprodukte durch vereinfachte Visualisierungen, animierte Grafiken und personalisierte Erklärungen verständlich machen.' : 
                  language === 'en' ? 'AI videos can make complex financial products understandable through simplified visualizations, animated graphics, and personalized explanations.' :
                  language === 'fr' ? 'Les vidéos IA peuvent rendre les produits financiers complexes compréhensibles grâce à des visualisations simplifiées, des graphiques animés et des explications personnalisées.' :
                  'I video AI possono rendere comprensibili prodotti finanziari complessi attraverso visualizzazioni semplificate, grafici animati e spiegazioni personalizzate.'
        },
        {
          question: language === 'de' ? 'Sind KI-Videos für Finanzdienstleister datenschutzkonform?' : 
                    language === 'en' ? 'Are AI videos for financial service providers compliant with data protection regulations?' :
                    language === 'fr' ? 'Les vidéos IA pour les prestataires de services financiers sont-elles conformes aux règlements de protection des données?' :
                    'I video AI per i fornitori di servizi finanziari sono conformi alle normative sulla protezione dei dati?',
          answer: language === 'de' ? 'Ja, unsere KI-Videos werden unter Einhaltung aller relevanten Datenschutzbestimmungen erstellt, mit besonderem Augenmerk auf die in der Finanzbranche geltenden Vorschriften.' : 
                  language === 'en' ? 'Yes, our AI videos are created in compliance with all relevant data protection regulations, with special attention to the regulations applicable in the financial industry.' :
                  language === 'fr' ? 'Oui, nos vidéos IA sont créées conformément à toutes les réglementations de protection des données pertinentes, avec une attention particulière aux réglementations applicables dans le secteur financier.' :
                  'Sì, i nostri video AI sono creati in conformità con tutte le normative sulla protezione dei dati pertinenti, con particolare attenzione alle normative applicabili nel settore finanziario.'
        },
        {
          question: language === 'de' ? 'Welche Arten von Finanzvideos können mit KI erstellt werden?' : 
                    language === 'en' ? 'What types of financial videos can be created with AI?' :
                    language === 'fr' ? 'Quels types de vidéos financières peuvent être créées avec l\'IA?' :
                    'Quali tipi di video finanziari possono essere creati con l\'AI?',
          answer: language === 'de' ? 'Mit KI können verschiedene Finanzvideos erstellt werden: Produkterklärungen, Anlagestrategien, Marktanalysen, Kundenreferenzen, Unternehmensvorstellungen und personalisierte Beratungsvideos.' : 
                  language === 'en' ? 'Various financial videos can be created with AI: product explanations, investment strategies, market analyses, customer testimonials, company presentations, and personalized advisory videos.' :
                  language === 'fr' ? 'Divers types de vidéos financières peuvent être créés avec l\'IA : explications de produits, stratégies d\'investissement, analyses de marché, témoignages de clients, présentations d\'entreprise et vidéos de conseil personnalisées.' :
                  'Vari video finanziari possono essere creati con l\'AI: spiegazioni di prodotti, strategie di investimento, analisi di mercato, testimonianze di clienti, presentazioni aziendali e video di consulenza personalizzati.'
        }
      ]
    },
    luxury: {
      name: language === 'de' ? 'KI-Videomarketing für Luxusmarken' :
            language === 'en' ? 'AI Video Marketing for Luxury Brands' :
            language === 'fr' ? 'Marketing Vidéo IA pour Marques de Luxe' :
            'Marketing Video AI per Marchi di Lusso',
      description: language === 'de' ? 'Premium KI-Videoinhalte für Luxusmarken und hochwertige Produkte' :
                  language === 'en' ? 'Premium AI video content for luxury brands and high-end products' :
                  language === 'fr' ? 'Contenu vidéo IA premium pour marques de luxe et produits haut de gamme' :
                  'Contenuto video AI premium per marchi di lusso e prodotti di alta gamma',
      keywords: 'Luxusmarken KI-Videos, Premium Produktvideos, Hochwertiges Videomarketing',
      serviceType: 'Luxury Brand Video Marketing',
      audience: 'Luxury Brands, Premium Product Manufacturers',
      faqs: [
        {
          question: language === 'de' ? 'Wie bewahren KI-Videos die Exklusivität von Luxusmarken?' : 
                    language === 'en' ? 'How do AI videos preserve the exclusivity of luxury brands?' :
                    language === 'fr' ? 'Comment les vidéos IA préservent-elles l\'exclusivité des marques de luxe?' :
                    'Come i video AI preservano l\'esclusività dei marchi di lusso?',
          answer: language === 'de' ? 'Unsere KI-Videos für Luxusmarken werden mit höchsten Qualitätsstandards und maßgeschneiderten Elementen erstellt, die die einzigartige Identität und Exklusivität der Marke bewahren und hervorheben.' : 
                  language === 'en' ? 'Our AI videos for luxury brands are created with the highest quality standards and customized elements that preserve and highlight the unique identity and exclusivity of the brand.' :
                  language === 'fr' ? 'Nos vidéos IA pour les marques de luxe sont créées avec les normes de qualité les plus élevées et des éléments personnalisés qui préservent et mettent en valeur l\'identité unique et l\'exclusivité de la marque.' :
                  'I nostri video AI per i marchi di lusso sono creati con i più alti standard di qualità e elementi personalizzati che preservano e mettono in evidenza l\'identità unica e l\'esclusività del marchio.'
        },
        {
          question: language === 'de' ? 'Können KI-Videos die Handwerkskunst von Luxusprodukten authentisch darstellen?' : 
                    language === 'en' ? 'Can AI videos authentically represent the craftsmanship of luxury products?' :
                    language === 'fr' ? 'Les vidéos IA peuvent-elles représenter authentiquement l\'artisanat des produits de luxe?' :
                    'I video AI possono rappresentare autenticamente l\'artigianato dei prodotti di lusso?',
          answer: language === 'de' ? 'Ja, durch hochauflösende Detailaufnahmen, strategische Bildkomposition und fachmännische Narration können unsere KI-Videos die Handwerkskunst, Materialqualität und Exklusivität von Luxusprodukten authentisch hervorheben.' : 
                  language === 'en' ? 'Yes, through high-resolution detail shots, strategic image composition, and expert narration, our AI videos can authentically highlight the craftsmanship, material quality, and exclusivity of luxury products.' :
                  language === 'fr' ? 'Oui, grâce à des prises de vue détaillées en haute résolution, une composition d\'image stratégique et une narration experte, nos vidéos IA peuvent mettre en valeur de manière authentique l\'artisanat, la qualité des matériaux et l\'exclusivité des produits de luxe.' :
                  'Sì, attraverso riprese di dettaglio ad alta risoluzione, composizione strategica dell\'immagine e narrazione esperta, i nostri video AI possono evidenziare autenticamente l\'artigianato, la qualità dei materiali e l\'esclusività dei prodotti di lusso.'
        }
      ]
    },
    tourism: {
      name: language === 'de' ? 'KI-Videomarketing für Tourismus' :
            language === 'en' ? 'AI Video Marketing for Tourism' :
            language === 'fr' ? 'Marketing Vidéo IA pour Tourisme' :
            'Marketing Video AI per Turismo',
      description: language === 'de' ? 'Inspirierende KI-Videos für Hotels, Reiseveranstalter und Tourismusverbände' :
                  language === 'en' ? 'Inspiring AI videos for hotels, tour operators, and tourism boards' :
                  language === 'fr' ? 'Vidéos IA inspirantes pour hôtels, voyagistes et offices du tourisme' :
                  'Video AI ispiranti per hotel, operatori turistici e enti del turismo',
      keywords: 'Tourismus KI-Videos, Hotel Marketingvideos, Reisevideos KI-erstellt',
      serviceType: 'Tourism Video Marketing',
      audience: 'Hotels, Tour Operators, Tourism Boards',
      faqs: [
        {
          question: language === 'de' ? 'Wie können KI-Videos Reisedestinationen emotional präsentieren?' : 
                    language === 'en' ? 'How can AI videos emotionally present travel destinations?' :
                    language === 'fr' ? 'Comment les vidéos IA peuvent-elles présenter émotionnellement les destinations de voyage?' :
                    'Come possono i video AI presentare emotivamente le destinazioni di viaggio?',
          answer: language === 'de' ? 'KI-Videos können durch immersive Bildsprache, atmosphärische Musik und personalisierte Szenarien die emotionalen Aspekte einer Reisedestination hervorheben und potenzielle Besucher inspirieren.' : 
                  language === 'en' ? 'AI videos can highlight the emotional aspects of a travel destination through immersive imagery, atmospheric music, and personalized scenarios to inspire potential visitors.' :
                  language === 'fr' ? 'Les vidéos IA peuvent mettre en évidence les aspects émotionnels d\'une destination de voyage grâce à des images immersives, une musique atmosphérique et des scénarios personnalisés pour inspirer les visiteurs potentiels.' :
                  'I video AI possono evidenziare gli aspetti emotivi di una destinazione di viaggio attraverso immagini immersive, musica atmosferica e scenari personalizzati per ispirare i potenziali visitatori.'
        },
        {
          question: language === 'de' ? 'Können KI-Videos verschiedene Zielgruppen im Tourismus ansprechen?' : 
                    language === 'en' ? 'Can AI videos target different audiences in tourism?' :
                    language === 'fr' ? 'Les vidéos IA peuvent-elles cibler différents publics dans le tourisme?' :
                    'I video AI possono rivolgersi a diversi pubblici nel turismo?',
          answer: language === 'de' ? 'Ja, KI-Videos können für verschiedene Touristengruppen wie Familien, Abenteuerreisende, Luxusurlauber oder Geschäftsreisende personalisiert werden, mit angepasstem Content, der deren spezifische Interessen und Bedürfnisse anspricht.' : 
                  language === 'en' ? 'Yes, AI videos can be personalized for different tourist groups such as families, adventure travelers, luxury vacationers, or business travelers, with customized content that addresses their specific interests and needs.' :
                  language === 'fr' ? 'Oui, les vidéos IA peuvent être personnalisées pour différents groupes de touristes tels que les familles, les voyageurs d\'aventure, les vacanciers de luxe ou les voyageurs d\'affaires, avec un contenu personnalisé qui répond à leurs intérêts et besoins spécifiques.' :
                  'Sì, i video AI possono essere personalizzati per diversi gruppi di turisti come famiglie, viaggiatori avventurosi, vacanzieri di lusso o viaggiatori d\'affari, con contenuti personalizzati che rispondono ai loro interessi e bisogni specifici.'
        }
      ]
    },
    healthcare: {
      name: language === 'de' ? 'KI-Videomarketing für Gesundheitswesen' :
            language === 'en' ? 'AI Video Marketing for Healthcare' :
            language === 'fr' ? 'Marketing Vidéo IA pour Santé' :
            'Marketing Video AI per Sanità',
      description: language === 'de' ? 'Vertrauenswürdige KI-Videos für Kliniken, Praxen und Gesundheitsdienstleister' :
                  language === 'en' ? 'Trustworthy AI videos for clinics, practices, and healthcare providers' :
                  language === 'fr' ? 'Vidéos IA fiables pour cliniques, cabinets et prestataires de santé' :
                  'Video AI affidabili per cliniche, studi medici e fornitori di servizi sanitari',
      keywords: 'Gesundheitswesen KI-Videos, Klinik Marketing Videos, Praxis Erklärvideo',
      serviceType: 'Healthcare Video Marketing',
      audience: 'Clinics, Medical Practices, Healthcare Providers',
      faqs: [
        {
          question: language === 'de' ? 'Wie stellen KI-Videos medizinische Informationen verständlich dar?' : 
                    language === 'en' ? 'How do AI videos present medical information in an understandable way?' :
                    language === 'fr' ? 'Comment les vidéos IA présentent-elles les informations médicales de manière compréhensible?' :
                    'Come i video AI presentano le informazioni mediche in modo comprensibile?',
          answer: language === 'de' ? 'KI-Videos können komplexe medizinische Informationen durch vereinfachte Animationen, klare Visualisierungen und laienverständliche Erklärungen zugänglich machen, ohne dabei fachliche Genauigkeit zu opfern.' : 
                  language === 'en' ? 'AI videos can make complex medical information accessible through simplified animations, clear visualizations, and explanations understandable to laypeople, without sacrificing technical accuracy.' :
                  language === 'fr' ? 'Les vidéos IA peuvent rendre les informations médicales complexes accessibles grâce à des animations simplifiées, des visualisations claires et des explications compréhensibles par les profanes, sans sacrifier la précision technique.' :
                  'I video AI possono rendere accessibili informazioni mediche complesse attraverso animazioni semplificate, visualizzazioni chiare e spiegazioni comprensibili ai non addetti ai lavori, senza sacrificare la precisione tecnica.'
        },
        {
          question: language === 'de' ? 'Sind KI-Videos für medizinische Einrichtungen datenschutzkonform?' : 
                    language === 'en' ? 'Are AI videos for medical facilities compliant with data protection regulations?' :
                    language === 'fr' ? 'Les vidéos IA pour les établissements médicaux sont-elles conformes aux règlementations sur la protection des données?' :
                    'I video AI per strutture mediche sono conformi alle normative sulla protezione dei dati?',
          answer: language === 'de' ? 'Ja, unsere KI-Videos für das Gesundheitswesen werden unter strenger Einhaltung der DSGVO und spezifischer Gesundheitsdatenschutzrichtlinien erstellt, um die Vertraulichkeit und Compliance zu gewährleisten.' : 
                  language === 'en' ? 'Yes, our AI videos for healthcare are created in strict compliance with GDPR and specific health data protection guidelines to ensure confidentiality and compliance.' :
                  language === 'fr' ? 'Oui, nos vidéos IA pour la santé sont créées dans le strict respect du RGPD et des directives spécifiques de protection des données de santé pour garantir la confidentialité et la conformité.' :
                  'Sì, i nostri video AI per la sanità sono creati in stretta conformità con il GDPR e le linee guida specifiche per la protezione dei dati sanitari per garantire riservatezza e conformità.'
        },
        {
          question: language === 'de' ? 'Für welche Bereiche im Gesundheitswesen eignen sich KI-Videos?' : 
                    language === 'en' ? 'For which areas in healthcare are AI videos suitable?' :
                    language === 'fr' ? 'Pour quels domaines de la santé les vidéos IA sont-elles adaptées?' :
                    'Per quali aree della sanità sono adatti i video AI?',
          answer: language === 'de' ? 'KI-Videos eignen sich für zahlreiche Bereiche im Gesundheitswesen: Patientenaufklärung, Behandlungserklärungen, Vorstellung von Fachärzten oder Kliniken, Präventionsinformationen, Reha-Anleitungen und zur Bewerbung von Gesundheitsdienstleistungen.' : 
                  language === 'en' ? 'AI videos are suitable for numerous areas in healthcare: patient education, treatment explanations, introduction of specialists or clinics, prevention information, rehabilitation instructions, and promotion of health services.' :
                  language === 'fr' ? 'Les vidéos IA conviennent à de nombreux domaines de la santé : éducation des patients, explications des traitements, présentation de spécialistes ou de cliniques, informations de prévention, instructions de réadaptation et promotion des services de santé.' :
                  'I video AI sono adatti a numerosi ambiti della sanità: educazione dei pazienti, spiegazioni dei trattamenti, presentazione di specialisti o cliniche, informazioni sulla prevenzione, istruzioni per la riabilitazione e promozione dei servizi sanitari.'
        }
      ]
    },
    ecommerce: {
      name: language === 'de' ? 'KI-Videomarketing für E-Commerce' :
            language === 'en' ? 'AI Video Marketing for E-Commerce' :
            language === 'fr' ? 'Marketing Vidéo IA pour E-Commerce' :
            'Marketing Video AI per E-Commerce',
      description: language === 'de' ? 'Konversionsstarke KI-Produktvideos für Online-Shops und E-Commerce' :
                  language === 'en' ? 'High-converting AI product videos for online shops and e-commerce' :
                  language === 'fr' ? 'Vidéos IA de produits à fort taux de conversion pour boutiques en ligne et e-commerce' :
                  'Video AI di prodotti ad alta conversione per negozi online e e-commerce',
      keywords: 'E-Commerce KI-Videos, Produktvideos Online-Shop, Conversion Produktvideos',
      serviceType: 'E-Commerce Video Marketing',
      audience: 'Online Retailers, E-Commerce Businesses',
      faqs: [
        {
          question: language === 'de' ? 'Wie steigern KI-Produktvideos die Conversion-Rate?' : 
                    language === 'en' ? 'How do AI product videos increase the conversion rate?' :
                    language === 'fr' ? 'Comment les vidéos de produits IA augmentent-elles le taux de conversion?' :
                    'Come i video di prodotti AI aumentano il tasso di conversione?',
          answer: language === 'de' ? 'KI-Produktvideos steigern die Conversion-Rate, indem sie Produkte aus verschiedenen Perspektiven zeigen, Funktionen anschaulich demonstrieren, Anwendungsbeispiele vorführen und durch emotionale Ansprache Kaufbarrieren abbauen.' : 
                  language === 'en' ? 'AI product videos increase the conversion rate by showing products from different perspectives, clearly demonstrating features, showcasing application examples, and reducing purchase barriers through emotional appeal.' :
                  language === 'fr' ? 'Les vidéos de produits IA augmentent le taux de conversion en montrant les produits sous différentes perspectives, en démontrant clairement les fonctionnalités, en présentant des exemples d\'application et en réduisant les barrières d\'achat grâce à un appel émotionnel.' :
                  'I video di prodotti AI aumentano il tasso di conversione mostrando i prodotti da diverse prospettive, dimostrando chiaramente le funzionalità, presentando esempi di applicazione e riducendo le barriere all\'acquisto attraverso l\'appeal emotivo.'
        },
        {
          question: language === 'de' ? 'Wie schnell können KI-Videos für neue Produkte erstellt werden?' : 
                    language === 'en' ? 'How quickly can AI videos be created for new products?' :
                    language === 'fr' ? 'À quelle vitesse les vidéos IA peuvent-elles être créées pour de nouveaux produits?' :
                    'Con quale rapidità possono essere creati video AI per nuovi prodotti?',
          answer: language === 'de' ? 'Mit unserem KI-Ansatz können Produktvideos in Stunden statt Tagen erstellt werden. Dies ermöglicht E-Commerce-Händlern, schnell auf Marktänderungen zu reagieren und neue Produkte umgehend mit professionellen Videos zu bewerben.' : 
                  language === 'en' ? 'With our AI approach, product videos can be created in hours instead of days. This allows e-commerce retailers to quickly respond to market changes and immediately advertise new products with professional videos.' :
                  language === 'fr' ? 'Avec notre approche IA, les vidéos de produits peuvent être créées en heures au lieu de jours. Cela permet aux détaillants e-commerce de réagir rapidement aux changements du marché et de promouvoir immédiatement de nouveaux produits avec des vidéos professionnelles.' :
                  'Con il nostro approccio AI, i video dei prodotti possono essere creati in ore anziché in giorni. Questo consente ai rivenditori di e-commerce di rispondere rapidamente ai cambiamenti del mercato e di pubblicizzare immediatamente nuovi prodotti con video professionali.'
        },
        {
          question: language === 'de' ? 'Können KI-Videos für den gesamten Produktkatalog erstellt werden?' : 
                    language === 'en' ? 'Can AI videos be created for the entire product catalog?' :
                    language === 'fr' ? 'Des vidéos IA peuvent-elles être créées pour l\'ensemble du catalogue de produits?' :
                    'I video AI possono essere creati per l\'intero catalogo prodotti?',
          answer: language === 'de' ? 'Ja, dank unserer skalierbaren KI-Technologie können wir Videos für den gesamten Produktkatalog eines Online-Shops erstellen, mit konsistentem Branding und individuellen Anpassungen pro Produktkategorie oder Zielgruppe.' : 
                  language === 'en' ? 'Yes, thanks to our scalable AI technology, we can create videos for an entire online shop\'s product catalog, with consistent branding and individual adjustments per product category or target audience.' :
                  language === 'fr' ? 'Oui, grâce à notre technologie IA évolutive, nous pouvons créer des vidéos pour l\'ensemble du catalogue de produits d\'une boutique en ligne, avec une image de marque cohérente et des ajustements individuels par catégorie de produit ou public cible.' :
                  'Sì, grazie alla nostra tecnologia AI scalabile, possiamo creare video per l\'intero catalogo prodotti di un negozio online, con un branding coerente e regolazioni individuali per categoria di prodotto o pubblico target.'
        }
      ]
    },
    general: {
      name: language === 'de' ? 'KI-Videomarketing für Unternehmen' :
            language === 'en' ? 'AI Video Marketing for Businesses' :
            language === 'fr' ? 'Marketing Vidéo IA pour Entreprises' :
            'Marketing Video AI per Aziende',
      description: language === 'de' ? 'Professionelle KI-generierte Videos für effektives Marketing und höhere Conversion' :
                  language === 'en' ? 'Professional AI-generated videos for effective marketing and higher conversion' :
                  language === 'fr' ? 'Vidéos professionnelles générées par IA pour un marketing efficace et une meilleure conversion' :
                  'Video professionali generati da IA per marketing efficace e conversione più elevata',
      keywords: 'KI-Videomarketing, AI-generierte Videos, Marketing Conversion Videos',
      serviceType: 'Business Video Marketing',
      audience: 'Companies, Organizations, Businesses of all sizes',
      faqs: [
        {
          question: language === 'de' ? 'Was sind die Vorteile von KI-generierten Videos gegenüber traditionellen Videos?' : 
                    language === 'en' ? 'What are the advantages of AI-generated videos over traditional videos?' :
                    language === 'fr' ? 'Quels sont les avantages des vidéos générées par l\'IA par rapport aux vidéos traditionnelles?' :
                    'Quali sono i vantaggi dei video generati dall\'AI rispetto ai video tradizionali?',
          answer: language === 'de' ? 'KI-generierte Videos bieten mehrere Vorteile: deutlich niedrigere Produktionskosten, schnellere Erstellung, einfache Aktualisierbarkeit, Mehrsprachigkeit ohne Neudreh und konsistente Qualität unabhängig vom Budget.' : 
                  language === 'en' ? 'AI-generated videos offer several advantages: significantly lower production costs, faster creation, easy updateability, multilingualism without re-shooting, and consistent quality regardless of budget.' :
                  language === 'fr' ? 'Les vidéos générées par l\'IA offrent plusieurs avantages : des coûts de production nettement inférieurs, une création plus rapide, une facilité de mise à jour, un multilinguisme sans nouveau tournage et une qualité constante quel que soit le budget.' :
                  'I video generati dall\'AI offrono diversi vantaggi: costi di produzione significativamente inferiori, creazione più rapida, facile aggiornabilità, multilinguismo senza riprese e qualità costante indipendentemente dal budget.'
        },
        {
          question: language === 'de' ? 'Wie personalisiert sind KI-generierte Videos?' : 
                    language === 'en' ? 'How personalized are AI-generated videos?' :
                    language === 'fr' ? 'Dans quelle mesure les vidéos générées par l\'IA sont-elles personnalisées?' :
                    'Quanto sono personalizzati i video generati dall\'AI?',
          answer: language === 'de' ? 'KI-generierte Videos können stark personalisiert werden, mit angepassten Inhalten, Sprechern, Szenarien, Markenfarben, Logos und sogar kundenspezifischen Elementen wie Produkten oder Dienstleistungen, die genau auf die Zielgruppe zugeschnitten sind.' : 
                  language === 'en' ? 'AI-generated videos can be highly personalized, with customized content, speakers, scenarios, brand colors, logos, and even customer-specific elements such as products or services that are tailored exactly to the target audience.' :
                  language === 'fr' ? 'Les vidéos générées par l\'IA peuvent être hautement personnalisées, avec du contenu, des présentateurs, des scénarios, des couleurs de marque, des logos et même des éléments spécifiques au client tels que des produits ou des services qui sont adaptés exactement au public cible.' :
                  'I video generati dall\'AI possono essere altamente personalizzati, con contenuti, presentatori, scenari, colori del marchio, loghi e persino elementi specifici del cliente come prodotti o servizi che sono adattati esattamente al pubblico target.'
        }
      ]
    }
  };
  
  const currentIndustry = industryDetails[industry] || industryDetails.general;
  
  // Create the schema object
  const schema: any = {
    "@context": "https://schema.org",
    "@type": "Service",
    "name": title || currentIndustry.name,
    "description": description || currentIndustry.description,
    "provider": {
      "@type": "Organization",
      "name": "Future Level",
      "url": "https://future-level.ch"
    },
    "serviceType": currentIndustry.serviceType,
    "audience": {
      "@type": "Audience",
      "audienceType": currentIndustry.audience
    },
    "inLanguage": language,
    "url": canonicalUrl,
    "image": imageUrl,
    "keywords": currentIndustry.keywords
  };
  
  // Add FAQ schema for the industry
  if (currentIndustry.faqs && currentIndustry.faqs.length > 0) {
    schema.mainEntity = currentIndustry.faqs.map(faq => ({
      "@type": "Question",
      "name": faq.question,
      "acceptedAnswer": {
        "@type": "Answer",
        "text": faq.answer
      }
    }));
  }
  
  // Add location-specific information if a city is provided
  if (city) {
    const cityCoordinates: {[key: string]: {lat: string, lng: string}} = {
      "zurich": { lat: "47.3769", lng: "8.5417" },
      "geneva": { lat: "46.2044", lng: "6.1432" },
      "basel": { lat: "47.5596", lng: "7.5886" },
      "bern": { lat: "46.9480", lng: "7.4474" },
      "lausanne": { lat: "46.5197", lng: "6.6323" },
      "lugano": { lat: "46.0036", lng: "8.9511" },
      "lucerne": { lat: "47.0502", lng: "8.3093" },
      "st-gallen": { lat: "47.4245", lng: "9.3767" }
    };
    
    const cityNames: {[key: string]: {de: string, en: string, fr: string, it: string}} = {
      "zurich": { de: "Zürich", en: "Zurich", fr: "Zurich", it: "Zurigo" },
      "geneva": { de: "Genf", en: "Geneva", fr: "Genève", it: "Ginevra" },
      "basel": { de: "Basel", en: "Basel", fr: "Bâle", it: "Basilea" },
      "bern": { de: "Bern", en: "Bern", fr: "Berne", it: "Berna" },
      "lausanne": { de: "Lausanne", en: "Lausanne", fr: "Lausanne", it: "Losanna" },
      "lucerne": { de: "Luzern", en: "Lucerne", fr: "Lucerne", it: "Lucerna" },
      "lugano": { de: "Lugano", en: "Lugano", fr: "Lugano", it: "Lugano" },
      "st-gallen": { de: "St. Gallen", en: "St. Gallen", fr: "Saint-Gall", it: "San Gallo" }
    };
    
    const coordinates = cityCoordinates[city.toLowerCase()] || cityCoordinates["zurich"];
    const cityName = cityNames[city.toLowerCase()] ? 
                     cityNames[city.toLowerCase()][language as keyof typeof cityNames[typeof city]] || 
                     city : 
                     city;
    
    schema.areaServed = {
      "@type": "City",
      "name": cityName
    };
    
    if (coordinates) {
      schema.availableAtOrFrom = {
        "@type": "Place",
        "name": cityName,
        "geo": {
          "@type": "GeoCoordinates",
          "latitude": coordinates.lat,
          "longitude": coordinates.lng
        }
      };
    }
    
    // Update the name to include the city
    schema.name = `${currentIndustry.name} ${cityName}`;
  }
  
  // Add the schema to the page
  let scriptTag = document.getElementById('industry-service-schema') as HTMLScriptElement | null;
  
  if (!scriptTag) {
    scriptTag = document.createElement('script');
    scriptTag.id = 'industry-service-schema';
    scriptTag.setAttribute('type', 'application/ld+json');
    document.head.appendChild(scriptTag);
  }
  
  scriptTag.textContent = JSON.stringify(schema);
};
